import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useBranch } from './context/BranchContext';
import Sidebar from './components/Layout/Sidebar';
import Navbar from './components/Layout/Navbar';
import GlobalNavbar from './components/Layout/GlobalNavbar';

// Lazy load all page components for code splitting
const Login = lazy(() => import('./pages/Auth/Login'));
const JoinNow = lazy(() => import('./pages/Auth/JoinNow'));
const Landing = lazy(() => import('./pages/Landing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CDS = lazy(() => import('./pages/CDS'));
const Settings = lazy(() => import('./pages/Settings'));
const POS = lazy(() => import('./pages/POS'));
const Reports = lazy(() => import('./pages/Reports'));
const Users = lazy(() => import('./pages/Users'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const BranchSelection = lazy(() => import('./pages/BranchSelection'));

// POS Module - Lazy loaded
const POSDashboard = lazy(() => import('./modules/pos/POSDashboard'));
const POSOrders = lazy(() => import('./modules/pos/POSOrders'));
const POSKitchen = lazy(() => import('./modules/pos/POSKitchen'));

// POS Context Provider (keep synchronous as it's needed immediately)
import { POSProvider } from './modules/pos/POSContext';

import './App.css';
import apiClient from './config/axios';
import { SocketProvider } from './context/SocketContext';
import { BranchProvider } from './context/BranchContext';

// Loading fallback component
const LoadingFallback = () => (
  <div className="loading" style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '16px',
    color: '#64748b'
  }}>
    Loading...
  </div>
);

// Error boundary for chunk loading errors
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a chunk loading error
    if (error.name === 'ChunkLoadError' || error.message?.includes('chunk')) {
      return { hasError: true, error };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    console.error('Chunk loading error:', error, errorInfo);
    // Reload page on chunk error
    if (error.name === 'ChunkLoadError' || error.message?.includes('chunk')) {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '16px',
          color: '#64748b'
        }}>
          <div>Loading new version...</div>
          <div style={{ fontSize: '12px', marginTop: '10px', color: '#94a3b8' }}>
            Please wait while we reload the page
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function PrivateRoute({ children }) {
  const { loading, token: authToken } = useAuth();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    console.log('[PRIVATE_ROUTE] useEffect triggered', { loading, authToken: !!authToken, path: location.pathname });
    
    // Wait for AuthContext to initialize
    if (loading) {
      console.log('[PRIVATE_ROUTE] AuthContext still loading, waiting...');
      return;
    }

    // Check token from multiple sources
    const localStorageToken = localStorage.getItem('token');
    const hasToken = localStorageToken || authToken;

    console.log('[PRIVATE_ROUTE] Token check:', {
      localStorageToken: !!localStorageToken,
      authToken: !!authToken,
      hasToken,
      path: location.pathname
    });

    if (hasToken) {
      console.log('[PRIVATE_ROUTE] ✅ Token found, allowing access');
      setIsAuthenticated(true);
      setIsValidating(false);
      
      // Save current route for refresh persistence
      const publicRoutes = ['/login', '/join-now', '/home', '/pricing', '/'];
      if (!publicRoutes.includes(location.pathname)) {
        localStorage.setItem('lastRoute', location.pathname);
      }
    } else {
      console.log('[PRIVATE_ROUTE] ❌ No token found, redirecting to login');
      setIsAuthenticated(false);
      setIsValidating(false);
      
      // Save the route they were trying to access
      if (location.pathname !== '/login') {
        localStorage.setItem('lastRoute', location.pathname);
      }
    }
  }, [loading, authToken, location.pathname]);

  // Show loading while validating
  if (loading || isValidating) {
    return <LoadingFallback />;
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated - render children
  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  const { selectedBranch } = useBranch();
  
  if (loading) return <LoadingFallback />;
  
  if (user) {
    // Check if branch is selected
    if (!selectedBranch) {
      return <Navigate to="/select-branch" replace />;
    }
    
    // Check for saved route in localStorage
    const savedRoute = localStorage.getItem('lastRoute');
    // Allow any valid route (not login/public routes)
    const publicRoutes = ['/login', '/join-now', '/home', '/pricing', '/', '/select-branch'];
    
    if (savedRoute && !publicRoutes.includes(savedRoute)) {
      // Clear saved route after using it
      localStorage.removeItem('lastRoute');
      return <Navigate to={savedRoute} replace />;
    }
    
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Landing />
    </Suspense>
  );
}

function MainLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const location = useLocation();
  const { selectedBranch } = useBranch();
  
  useEffect(() => {
    const handleResize = () => setSidebarOpen(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save current route to localStorage whenever route changes
  useEffect(() => {
    const publicRoutes = ['/login', '/join-now', '/home', '/pricing', '/', '/select-branch'];
    if (!publicRoutes.includes(location.pathname)) {
      localStorage.setItem('lastRoute', location.pathname);
    }
  }, [location.pathname]);

  // Redirect to branch selection if no branch selected
  useEffect(() => {
    if (!selectedBranch && !['/login', '/join-now', '/home', '/pricing', '/', '/select-branch'].includes(location.pathname)) {
      window.location.href = '/select-branch';
    }
  }, [selectedBranch, location.pathname]);

  return (
    <div className="app-container">
      <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="main-content">
        <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className="content-wrapper">
          {children}
        </div>
      </div>
    </div>
  );
}

// POS Layout - No sidebar, just content
function POSMainLayout({ children }) {
  const location = useLocation();
  
  // Save current route to localStorage whenever route changes
  useEffect(() => {
    const publicRoutes = ['/login', '/join-now', '/home', '/pricing', '/'];
    if (!publicRoutes.includes(location.pathname)) {
      localStorage.setItem('lastRoute', location.pathname);
    }
  }, [location.pathname]);

  return (
    <div className="app-container">
      {/* Sidebar is completely hidden for POS pages */}
      <div className="main-content" style={{ marginLeft: 0, width: '100%' }}>
        <Navbar onMenuClick={() => {}} />
        <div className="content-wrapper">
          {children}
        </div>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    apiClient.get('/health').then(r => {
      console.log('API health:', r.data);
    }).catch(err => {
      console.warn('API health check failed:', err?.message || err);
    });
  }, []);

  return (
    <ChunkErrorBoundary>
      <AuthProvider>
        <BranchProvider>
          <SocketProvider>
            <GlobalNavbar />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <Routes>
        <Route path="/home" element={<Suspense fallback={<LoadingFallback />}><Landing /></Suspense>} />
        <Route path="/pricing" element={<Suspense fallback={<LoadingFallback />}><PricingPage /></Suspense>} />
        <Route path="/join-now" element={<Suspense fallback={<LoadingFallback />}><JoinNow /></Suspense>} />
        <Route path="/login" element={<Suspense fallback={<LoadingFallback />}><Login /></Suspense>} />
        <Route path="/client-login/:loginUrl" element={<Suspense fallback={<LoadingFallback />}><Login /></Suspense>} />
        <Route path="/client-login" element={<Suspense fallback={<LoadingFallback />}><Login /></Suspense>} />
        <Route path="/" element={<HomeRoute />} />

        {/* Branch Selection Route */}
        <Route path="/select-branch" element={<PrivateRoute><BranchSelection /></PrivateRoute>} />

        {/* Main App Routes */}
        <Route path="/dashboard" element={<PrivateRoute><MainLayout><Dashboard /></MainLayout></PrivateRoute>} />
        <Route path="/cds" element={<PrivateRoute><CDS /></PrivateRoute>} />
        {/* POS Module Routes - All tabs within single dashboard */}
        <Route 
          path="/pos" 
          element={
            <PrivateRoute>
              <POSMainLayout>
                <POSDashboard />
              </POSMainLayout>
            </PrivateRoute>
          } 
        />
        
        {/* New Order Screen - Full Page */}
        <Route 
          path="/pos/new-order" 
          element={
            <PrivateRoute>
              <POSMainLayout>
                <POSOrders />
              </POSMainLayout>
            </PrivateRoute>
          } 
        />
        
        {/* Kitchen Display System - Separate route */}
        <Route 
          path="/pos/kitchen" 
          element={
            <PrivateRoute>
              <MainLayout>
                <POSKitchen />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        {/* Legacy POS route - redirect to new POS Dashboard */}
        <Route 
          path="/pos/old" 
          element={
            <PrivateRoute>
              <MainLayout>
                <POS />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        <Route path="/reports" element={<PrivateRoute><MainLayout><Reports /></MainLayout></PrivateRoute>} />
        <Route path="/users" element={<PrivateRoute><MainLayout><Users /></MainLayout></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><MainLayout><Settings /></MainLayout></PrivateRoute>} />

        <Route path="*" element={<Navigate to="/home" />} />
            </Routes>
          </SocketProvider>
        </BranchProvider>
      </AuthProvider>
    </ChunkErrorBoundary>
  );
}

export default App;
